import React, { useState, FormEvent, FocusEvent, useRef, useEffect, MutableRefObject } from 'react';
import { ButtonVariant } from 'react-magma-dom';
import { ClearIcon } from 'react-magma-icons';
import Draggable, { DraggableEventHandler } from 'react-draggable';
import { useMainContentSize } from '../../hooks/useMainContentSize';
import { getProportion, getPixelsFromPercents, getCursor, getVerbWidth, Axis } from '../../utils';
import { StyledDraggableLabel } from '../Styled';
import { StyledLabelContent, StyledRemoveBtn } from '../Styled/StyledMainContent';
import { OFFSET_PARENT, LABEL_TYPE, dragStart, OnContentChange } from '../../utils/draggable';
import { ISelectionRange, IPosition } from '../../types';

interface IDraggableLabel {
  position: IPosition;
  text: string;
  selectionRange: ISelectionRange;
  id: number;
  labelColor: string;
  isFocussed: boolean;
  isEditMode: boolean;
  isHidden: boolean;
  isManuallyHidden: boolean;
  isHighlighted: boolean;
  handleStop: (id: number, position: IPosition, text: string, selectionStart: number) => void;
  handleBlur: (id: number, event: FocusEvent<HTMLInputElement>) => void;
  handleRemove: (id: number, event?: FocusEvent<HTMLInputElement>) => void;
}

export const DraggableLabel: React.FC<IDraggableLabel> = (props): JSX.Element => {
  const {
    selectionRange,
    id,
    text,
    labelColor,
    position,
    isFocussed,
    isEditMode,
    isHidden,
    isManuallyHidden,
    isHighlighted,
    handleStop,
    handleBlur,
    handleRemove,
  } = props;

  const [isInMove, setMove] = useState(false);
  const [content, setContent] = useState(text);
  const [x, y] = useMainContentSize();
  const [bounds, setBounds] = useState({ left: 0, right: 0, top: 0, bottom: 0 });

  const verbContainerRef = useRef() as MutableRefObject<HTMLDivElement>;
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
  const { selectionStart, width } = selectionRange;

  useEffect(() => {
    if (verbContainerRef.current) {
      setContainerDimensions({
        width: verbContainerRef.current.offsetWidth,
        height: verbContainerRef.current.offsetHeight,
      });
    }
  }, []);

  const handleDragStop: DraggableEventHandler = (_, data) => {
    const { node } = data;
    const value = (node?.firstChild as HTMLInputElement).value;
    const position = {
      x: getProportion(data.x, x),
      y: getProportion(data.y, y),
    };

    const target = node.firstChild as HTMLInputElement;
    setContent(value);
    handleStop(id, position, value, target.selectionStart as number);

    return false;
  };

  const handleStart: DraggableEventHandler = (_, data) => {
    dragStart(data, OFFSET_PARENT, setBounds);
  };

  const onChange = (event: FormEvent<HTMLInputElement>) => {
    const isSuccess = OnContentChange(event, setContent, LABEL_TYPE);
    const target = event.target as HTMLInputElement;
    isSuccess ? (target.style.width = getVerbWidth(target.value)) : null;
    verbContainerRef.current &&
      setContainerDimensions({
        width: verbContainerRef.current.offsetWidth,
        height: verbContainerRef.current.offsetHeight,
      });
  };

  const onFocus = (event: FormEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;

    target.selectionStart = target.selectionEnd = selectionStart != undefined ? selectionStart : 0;
  };

  return (
    <Draggable
      handle=".handle"
      onStart={handleStart}
      onStop={handleDragStop}
      onDrag={() => setMove(true)}
      bounds={bounds}
      positionOffset={{ x: '-50%', y: '-50%' }}
      position={{
        x: getPixelsFromPercents(x, position.x, containerDimensions.width, Axis.x),
        y: getPixelsFromPercents(y, position.y, containerDimensions.height, Axis.y),
      }}
    >
      <StyledDraggableLabel
        data-testid="draggable-label"
        labelColor={labelColor}
        isHidden={isHidden || isManuallyHidden}
        isHighlighted={isHighlighted}
        ref={verbContainerRef}
      >
        <StyledLabelContent
          data-testid="draggable-label-input"
          tabIndex={-1}
          className="handle"
          style={{ width, cursor: getCursor(isInMove) }}
          value={content}
          onChange={onChange}
          onBlur={(event: FocusEvent<HTMLInputElement>) => handleBlur(id, event)}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={isFocussed}
          onFocus={onFocus}
          readOnly={!isEditMode || isInMove}
        />
        {isEditMode ? (
          <StyledRemoveBtn
            testId="remove-label-btn"
            tabIndex={-1}
            onMouseUp={() => handleRemove(id)}
            aria-label=""
            variant={ButtonVariant.solid}
            icon={<ClearIcon size={15} />}
          />
        ) : (
          <></>
        )}
      </StyledDraggableLabel>
    </Draggable>
  );
};
